import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  Image,
  Italic,
  Link,
  List,
  Markdown,
  Paragraph,
  Table,
  TableToolbar,
} from 'ckeditor5';
import { Button, TextInput } from '@opendate-app/setlist';
import humanizeSlug from '../utils/humanizeSlug';

const editorConfig = () => ({
  licenseKey: window.gon?.ckeditor_license_key,
  plugins: [
    Autoformat,
    AutoLink,
    BlockQuote,
    Bold,
    Essentials,
    Heading,
    Image,
    Italic,
    Link,
    List,
    Markdown,
    Paragraph,
    Table,
    TableToolbar,
  ],
  toolbar: ['heading', '|', 'bold', 'italic', 'link', '|', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', '|', 'undo', 'redo'],
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading2', view: 'h2', title: 'Heading', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Subheading', class: 'ck-heading_heading3' },
    ],
  },
  table: { contentToolbar: ['tableColumn', 'tableRow'] },
});

const UserManualArticleEditor = ({ slug, onSaved, onCancel }) => {
  const [article, setArticle] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [title, setTitle] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setLoadError(false);
    setSaveError(null);
    setDirty(false);
    axios
      .get(`/user_manual/drafts/${slug}.json`)
      .then((response) => {
        if (cancelled) return;
        setArticle(response.data);
        setTitle(response.data.title);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => { cancelled = true; };
  }, [slug, reloadToken]);

  const handleSave = () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    setSaving(true);
    setSaveError(null);
    axios
      .patch(
        `/user_manual/articles/${slug}.json`,
        { title, body: editorRef.current.getData(), base_sha: article.sha },
        { headers: { 'X-CSRF-Token': csrfToken } },
      )
      .then((response) => {
        setSaving(false);
        toast.success(response.data.published ? 'Saved. Publish it to update what venues see.' : 'Saved.');
        onSaved(slug, response.data);
      })
      .catch((error) => {
        setSaving(false);
        setSaveError({
          message: error.response?.data?.error || "Couldn't save. Try again.",
          stale: error.response?.status === 409,
        });
      });
  };

  return (
    <div className="user-manual-editor">
      <div className="user-manual-editor-bar">
        <span className="user-manual-editor-bar-label">
          {article ? `Editing · ${humanizeSlug(article.section)} › ${humanizeSlug(article.chapter)}` : 'Loading article…'}
        </span>
        <div className="user-manual-editor-bar-actions">
          <Button size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button size="sm" variant="primary" onClick={handleSave} disabled={!article || !dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
      {saveError && (
        <div className="user-manual-draft-banner user-manual-draft-banner-error">
          <span>{saveError.message}</span>
          {saveError.stale && (
            <Button size="sm" onClick={() => setReloadToken((token) => token + 1)}>Load latest</Button>
          )}
        </div>
      )}
      {loadError && (
        <div className="user-manual-draft-banner user-manual-draft-banner-error">
          <span>Couldn't load this article for editing.</span>
          <Button size="sm" onClick={() => setReloadToken((token) => token + 1)}>Try again</Button>
        </div>
      )}
      {article && (
        <>
          <TextInput
            label="Title"
            value={title}
            onChange={(event) => { setTitle(event.target.value); setDirty(true); }}
          />
          <div className="user-manual-editor-body user-manual-article-body">
            <CKEditor
              key={article.sha}
              editor={ClassicEditor}
              config={editorConfig()}
              data={article.body}
              onReady={(editor) => { editorRef.current = editor; }}
              onChange={() => setDirty(true)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UserManualArticleEditor;
